import { useState } from 'react';

interface DonationProps {
  showForm: boolean;
  onClose: () => void;
}

export default function Donation({ showForm, onClose }: DonationProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(150);
  const [customAmount, setCustomAmount] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const donationOptions = [
    { amount: 150, baskets: 1, label: '1 Bakul Makanan' },
    { amount: 300, baskets: 2, label: '2 Bakul Makanan' },
    { amount: 750, baskets: 5, label: '5 Bakul Makanan' },
    { amount: 1500, baskets: 10, label: '10 Bakul Makanan' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowThankYou(true);
    setTimeout(() => {
      setShowThankYou(false);
      onClose();
    }, 3000);
  };

  return (
    <section id="infaq" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-amber-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
            <i className="ri-hand-heart-line"></i>
            Infaq & Sumbangan
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-4">
            💖 Sertai & Sokong Kempen Infaq
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Setiap sumbangan anda amat bermakna untuk membantu bekas wira negara
          </p>
        </div>

        {/* Donation Options */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {donationOptions.map((option, index) => (
            <div 
              key={index}
              onClick={() => setSelectedAmount(option.amount)}
              className={`relative bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 ${
                selectedAmount === option.amount ? 'border-emerald-500 scale-105' : 'border-gray-100'
              }`}
            >
              {selectedAmount === option.amount && (
                <div className="absolute -top-3 -right-3 w-7 h-7 sm:w-8 sm:h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-white text-base sm:text-lg"></i>
                </div>
              )}
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-shopping-basket-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">RM{option.amount}</div>
                <div className="text-xs sm:text-sm text-gray-600 mb-1">{option.label}</div>
                <div className="text-xs text-emerald-600 font-semibold">{option.baskets} keluarga terbantu</div>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="max-w-2xl mx-auto mb-8 sm:mb-12">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 text-center">
              Atau Sumbangan Ikhlas Mengikut Kemampuan
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <input 
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                  }}
                  placeholder="Masukkan jumlah (RM)"
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-base sm:text-lg"
                />
              </div>
              <button 
                onClick={() => setSelectedAmount(Number(customAmount))}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Pilih
              </button>
            </div>
          </div>
        </div>

        {/* Impact Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-6 sm:p-10 text-white shadow-2xl mb-8 sm:mb-12">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">📊 Impak Sumbangan Anda</h3>
            <p className="text-sm sm:text-lg text-emerald-50">
              Lihat bagaimana sumbangan anda memberi impak kepada masyarakat
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 text-center">
              <div className="text-3xl sm:text-5xl font-bold text-amber-300 mb-2">RM750,000</div>
              <div className="text-xs sm:text-sm text-emerald-100">Nilai Keseluruhan Bantuan</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 text-center">
              <div className="text-3xl sm:text-5xl font-bold text-amber-300 mb-2">5,000+</div>
              <div className="text-xs sm:text-sm text-emerald-100">Keluarga Terbantu</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 text-center">
              <div className="text-3xl sm:text-5xl font-bold text-amber-300 mb-2">100%</div>
              <div className="text-xs sm:text-sm text-emerald-100">Telus & Beramanah</div>
            </div>
          </div>
        </div>

        {/* Trust Section */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-xl border border-gray-100 mb-8 sm:mb-12">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">🕊️ Ketelusan & Amanah</h3>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-emerald-600"></i>
              </div>
              <h4 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Pengurusan Telus</h4>
              <p className="text-xs sm:text-sm text-gray-600">Setiap sumbangan diurus secara telus dan beramanah</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-file-list-3-line text-2xl sm:text-3xl text-amber-600"></i>
              </div>
              <h4 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Agihan Teratur</h4>
              <p className="text-xs sm:text-sm text-gray-600">Diselaraskan oleh PBPKNM mengikut kelayakan</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-file-chart-line text-2xl sm:text-3xl text-teal-600"></i>
              </div>
              <h4 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Laporan Ringkas</h4>
              <p className="text-xs sm:text-sm text-gray-600">Laporan agihan disediakan kepada penyumbang</p>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
          <h3 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">📢 Ayuh Bersama Menyumbang</h3>
          <p className="text-lg sm:text-2xl text-amber-50 mb-4 sm:mb-6 font-medium">
            "Hulurkan Infaq, Ringankan Beban, Kongsi Rezeki"
          </p>
          <p className="text-sm sm:text-lg text-amber-100 mb-6 sm:mb-8 max-w-3xl mx-auto">
            Sumbangan anda bukan sekadar bakul makanan, tetapi tanda penghargaan kepada jasa dan pengorbanan bekas wira negara.
          </p>
          <button 
            onClick={() => {}}
            className="group px-8 sm:px-12 py-4 sm:py-5 bg-white text-amber-600 text-lg sm:text-xl font-bold rounded-full shadow-2xl hover:shadow-white/30 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center justify-center gap-2 sm:gap-3">
              <i className="ri-hand-heart-fill text-xl sm:text-2xl"></i>
              INFAQ SEKARANG
              <i className="ri-arrow-right-line text-xl sm:text-2xl group-hover:translate-x-2 transition-transform"></i>
            </span>
          </button>
          <p className="text-xs sm:text-sm text-amber-100 mt-4 sm:mt-6">
            <i className="ri-information-line"></i> Semoga setiap infaq menjadi amal jariah yang berpanjangan dan diberkati Allah SWT
          </p>
        </div>
      </div>

      {/* Donation Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 sm:p-6 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl sm:text-2xl font-bold">Borang Sumbangan</h3>
                <button 
                  onClick={onClose}
                  className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl sm:text-2xl"></i>
                </button>
              </div>
            </div>

            {!showThankYou ? (
              <form onSubmit={handleSubmit} className="p-6 sm:p-8">
                <div className="mb-6">
                  <div className="bg-emerald-50 rounded-2xl p-4 sm:p-6 text-center border border-emerald-200">
                    <div className="text-xs sm:text-sm text-gray-600 mb-2">Jumlah Sumbangan</div>
                    <div className="text-3xl sm:text-4xl font-bold text-emerald-600">
                      RM{selectedAmount || customAmount || 0}
                    </div>
                    {selectedAmount && selectedAmount >= 150 && (
                      <div className="text-xs sm:text-sm text-gray-600 mt-2">
                        ≈ {Math.floor(selectedAmount / 150)} bakul makanan
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Nama Penuh</label>
                    <input 
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-sm sm:text-base"
                      placeholder="Masukkan nama penuh anda"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Emel</label>
                    <input 
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-sm sm:text-base"
                      placeholder="contoh@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Nombor Telefon</label>
                    <input 
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-sm sm:text-base"
                      placeholder="01X-XXX XXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Mesej (Pilihan)</label>
                    <textarea 
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none resize-none text-sm sm:text-base"
                      placeholder="Tinggalkan mesej sokongan anda..."
                    ></textarea>
                  </div>
                </div>

                <button 
                  type="submit"
                  className="w-full mt-6 sm:mt-8 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-base sm:text-lg font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <span className="flex items-center justify-center gap-2 sm:gap-3">
                    <i className="ri-hand-heart-line text-xl sm:text-2xl"></i>
                    Teruskan ke Pembayaran
                  </span>
                </button>
              </form>
            ) : (
              <div className="p-8 sm:p-12 text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className="ri-check-line text-4xl sm:text-5xl text-emerald-600"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">Terima Kasih!</h3>
                <p className="text-base sm:text-lg text-gray-600">
                  Sumbangan anda amat dihargai dan akan membantu bekas wira negara.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
